import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { findByCodTurmaAndCodCronogramaData, saveAll } from "./api-cronograma";
import { dateFormat, dayOfWeekName, timeFormat } from "../../util/DateUtil";
import { errorMessage } from "../../util/ConfirmAlertUtil";
import { success } from "../../util/ToastUtil";
import Loading from "../UI/Loading";
import { mensagemDe, porDiaEHorario } from "./cronogramaUtil";

/** Diálogo para copiar aulas do período anterior para o período atual. */
export default function CronogramaTableSelect({ open, onClose, turma, periodoAnterior, periodoAtual, onCopied }) {
  const [cronogramas, setCronogramas] = useState([]);
  const [selecionados, setSelecionados] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open || !periodoAnterior) return undefined;
    let active = true;
    setIsLoading(true);
    setSelecionados(new Set());
    findByCodTurmaAndCodCronogramaData(turma.codigo, periodoAnterior.codigo)
      .then((data) => {
        if (!active || !data) return;
        if (data.error) {
          errorMessage(mensagemDe(data));
        } else if (Array.isArray(data)) {
          setCronogramas([...data].sort(porDiaEHorario));
        }
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [open, turma.codigo, periodoAnterior]);

  const toggle = (codigo) =>
    setSelecionados((prevState) => {
      const next = new Set(prevState);
      if (next.has(codigo)) next.delete(codigo);
      else next.add(codigo);
      return next;
    });

  const todosMarcados = cronogramas.length > 0 && selecionados.size === cronogramas.length;

  const handleCopiar = () => {
    const persist = cronogramas
      .filter((c) => selecionados.has(c.codigo))
      .map((c) => ({ ...c, codigo: null, cronogramaData: periodoAtual }));
    setIsSaving(true);
    saveAll(persist)
      .then((data) => {
        if (!data || data.error) {
          errorMessage(mensagemDe(data, "Não foi possível copiar as aulas."));
          return;
        }
        success(`${persist.length} aula(s) copiada(s).`);
        onCopied();
      })
      .finally(() => setIsSaving(false));
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        Copiar Aulas do Período Anterior
        {periodoAnterior && (
          <Typography variant="body2" color="text.secondary">
            De {dateFormat(periodoAnterior.dataInicio)}
            {periodoAnterior.dataFim ? ` até ${dateFormat(periodoAnterior.dataFim)}` : ""} para o período atual
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {isLoading ? (
          <Box py={4}>
            <Loading size={28} description="Carregando aulas..." />
          </Box>
        ) : cronogramas.length === 0 ? (
          <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
            O período anterior não tem aulas cadastradas.
          </Typography>
        ) : (
          <TableContainer sx={{ border: 1, borderColor: "divider", borderRadius: 2 }}>
            <Table size="small" stickyHeader aria-label="Aulas do período anterior">
              <TableHead>
                <TableRow sx={{ "& th": { fontWeight: 600 } }}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={todosMarcados}
                      indeterminate={selecionados.size > 0 && !todosMarcados}
                      onChange={() =>
                        setSelecionados(todosMarcados ? new Set() : new Set(cronogramas.map((c) => c.codigo)))
                      }
                      slotProps={{ input: { "aria-label": "Marcar todas" } }}
                    />
                  </TableCell>
                  <TableCell>Dia</TableCell>
                  <TableCell>Aula</TableCell>
                  <TableCell>Disciplina</TableCell>
                  <TableCell>Professor</TableCell>
                  <TableCell>Local</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cronogramas.map((c) => (
                  <TableRow key={c.codigo} hover onClick={() => toggle(c.codigo)} sx={{ cursor: "pointer" }}>
                    <TableCell padding="checkbox">
                      <Checkbox checked={selecionados.has(c.codigo)} tabIndex={-1} />
                    </TableCell>
                    <TableCell>{dayOfWeekName(c.diaSemana)}</TableCell>
                    <TableCell>
                      {c.cronogramaAula?.ordemAula}ª · {timeFormat(c.cronogramaAula?.horarioInicio)} -{" "}
                      {timeFormat(c.cronogramaAula?.horarioFim)}
                    </TableCell>
                    <TableCell>{c.tdp?.disciplina?.descricao}</TableCell>
                    <TableCell>{c.tdp?.usuario?.nome ?? "--"}</TableCell>
                    <TableCell>{c.local?.descricao ?? "--"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" disabled={selecionados.size === 0 || isSaving} onClick={handleCopiar}>
          Copiar {selecionados.size > 0 ? selecionados.size : ""} aula(s)
        </Button>
      </DialogActions>
    </Dialog>
  );
}
